import { motion } from 'framer-motion';
import { SectionShell } from '@/components/pitch/SectionShell';
import {
  PRODUCT_PILLARS,
  type ProductPillar,
} from '@/components/pitch/product-content';
import { GovernedCaseRail } from './GovernedCaseRail';
import { type GovernedCasePhase } from './governed-case-state';

interface PitchV5ProductProps {
  phase: GovernedCasePhase;
}

/**
 * V5 keeps the V1 product copy verbatim while the governed rail supplies the
 * product explanation without turning any informational pillar into a control.
 */
export function PitchV5Product({ phase }: PitchV5ProductProps) {
  return (
    <SectionShell
      id="product"
      index={3}
      density="open"
      eyebrow="The product"
      title={
        <>
          One workflow.{' '}
          <span className="text-brand-bright">Six case types.</span> AI
          throughout
        </>
      }
      intro="Asset Hound is the AI workflow platform for UK insolvency practitioners. One unified, audited system with AI at its core, built for the regulator, not just the user"
    >
      <div className="pitch-v5-product-proof" aria-hidden>
        <GovernedCaseRail phase={phase} className="pitch-v5-product-rail" />
      </div>

      <div className="pitch-v5-product-grid pitch-strip grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-border bg-border-subtle md:grid-cols-2 lg:grid-cols-3">
        {PRODUCT_PILLARS.map((pillar, index) => (
          <PillarCell key={pillar.id} pillar={pillar} index={index} />
        ))}
      </div>
    </SectionShell>
  );
}

function PillarCell({
  pillar,
  index,
}: {
  pillar: ProductPillar;
  index: number;
}) {
  const commonProps = {
    initial: { opacity: 0, y: 12 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.3 },
    transition: {
      duration: 0.5,
      ease: [0.16, 1, 0.3, 1] as [number, number, number, number],
      delay: 0.05 + index * 0.06,
    },
  };

  const content = (
    <>
      <span className="text-[11px] font-medium uppercase tabular-nums tracking-[0.22em] text-brand-bright">
        {pillar.num}
      </span>
      <h3 className="text-balance text-[20px] font-semibold leading-snug text-ink-strong sm:text-[22px]">
        {pillar.title}
      </h3>
      <p className="text-[14px] leading-relaxed text-ink-muted">
        {pillar.body}
      </p>
    </>
  );

  return (
    <motion.div
      {...commonProps}
      className="pitch-cell pitch-cell-interactive flex flex-col gap-4 p-7 sm:p-8"
    >
      {content}
    </motion.div>
  );
}
